import { useEffect, useRef } from "react";
import { useScrollY } from "@/hooks/useParallax";

const marqueeItems = [
  "Head of Product",
  "Head of Engineering",
  "Cloud Architect",
  "15+ Years Experience",
  "Agentic AI",
  "Design Thinking",
  "Extreme Ownership",
  "Scrum Master",
  "AWS · Terraform · Docker",
  "Kotlin · Python · SQL",
];

export default function Hero() {
  const textRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);
  const scrollY = useScrollY();

  useEffect(() => {
    [textRef.current, photoRef.current].forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(32px)";
      setTimeout(() => {
        el.style.transition = "opacity 0.9s ease-out, transform 0.9s ease-out";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, 100 + i * 180);
    });
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const photoParallax = scrollY * 0.09;
  const bgParallax = scrollY * 0.04;

  return (
    <section className="relative min-h-screen w-full overflow-hidden flex flex-col" style={{ backgroundColor: "#140F0A" }}>

      {/* Background texture grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      {/* Warm amber glow — top right */}
      <div
        className="absolute top-[-120px] right-[-80px] w-[500px] h-[500px] rounded-full pointer-events-none will-change-transform"
        style={{
          background: "radial-gradient(circle, rgba(193,125,60,0.12) 0%, transparent 70%)",
          transform: `translateY(${bgParallax}px)`,
        }}
      />
      {/* Deep warm glow — bottom left */}
      <div
        className="absolute bottom-[-80px] left-[-60px] w-[400px] h-[400px] rounded-full pointer-events-none will-change-transform"
        style={{
          background: "radial-gradient(circle, rgba(92,74,50,0.25) 0%, transparent 70%)",
          transform: `translateY(-${bgParallax * 0.6}px)`,
        }}
      />

      {/* Main content — fills screen */}
      <div className="flex-1 flex items-center">
        <div className="w-full mx-auto px-8 lg:px-16" style={{ maxWidth: "1200px", paddingTop: "80px" }}>

          {/* Desktop: photo left, amber rule, text right */}
          <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-0">

            {/* PHOTO — left */}
            <div
              ref={photoRef}
              className="relative flex-shrink-0 flex justify-center lg:justify-start"
            >
              {/* Amber corner accent */}
              <div
                className="absolute z-0"
                style={{
                  top: "-16px",
                  left: "-16px",
                  width: "120px",
                  height: "120px",
                  border: "1px solid rgba(193,125,60,0.3)",
                  borderRadius: "4px",
                }}
              />

              {/* Photo */}
              <div
                className="relative z-10 overflow-hidden"
                style={{
                  width: "min(360px, 85vw)",
                  height: "min(460px, 108vw)",
                  borderRadius: "4px 80px 4px 80px",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/661f58d1-4775-43ab-846c-b8afe4ff99b3/c670e810-e1eb-4462-a539-033441a567e1_openart-image_1775278207664_1e913918_1775278207746_ff2d7412.png?v=20273c4bfba546da25c37b8ed2bffe65"
                  alt="Josue Tello — Product &amp; Engineering Leader"
                  title="Josue Tello headshot"
                  className="w-full h-full object-cover object-top"
                />
                {/* Subtle dark vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#140F0A]/40 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Floating stat — bottom right of photo */}
              <div
                className="absolute z-20"
                style={{
                  bottom: "24px",
                  right: "-20px",
                  backgroundColor: "#C17D3C",
                  borderRadius: "12px",
                  padding: "14px 18px",
                }}
              >
                <p className="font-lora font-semibold text-[26px] leading-none text-[#FAF8F4]">15+</p>
                <p className="font-dm text-[11px] text-[#FAF8F4]/70 leading-snug mt-1">Years in<br />tech leadership</p>
              </div>

              {/* Patent badge — top right */}
              <div
                className="absolute z-20"
                style={{
                  top: "24px",
                  right: "-24px",
                  backgroundColor: "rgba(250,248,244,0.06)",
                  border: "1px solid rgba(250,248,244,0.12)",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  backdropFilter: "blur(8px)",
                }}
              >
                <p className="font-dm text-[10px] uppercase tracking-[0.1em] text-[#C4B49A] mb-0.5">US Patent</p>
                <p className="font-dm font-semibold text-[13px] text-[#FAF8F4]">#11438733</p>
              </div>
            </div>

            {/* AMBER VERTICAL RULE — desktop only */}
            <div
              className="hidden lg:block flex-shrink-0 mx-14"
              style={{ width: "1px", height: "320px", backgroundColor: "#C17D3C", opacity: 0.35 }}
            />

            {/* TEXT — right */}
            <div ref={textRef} className="flex-1 min-w-0">
              {/* Open to Work badge */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-dm text-[12px] font-medium"
                  style={{
                    backgroundColor: "rgba(74, 180, 100, 0.1)",
                    border: "1px solid rgba(74, 180, 100, 0.25)",
                    color: "#6FCF8A",
                  }}
                >
                  <span className="relative flex items-center justify-center w-2 h-2">
                    <span
                      className="absolute inline-flex w-full h-full rounded-full opacity-75"
                      style={{
                        backgroundColor: "#4AB464",
                        animation: "ping 1.6s cubic-bezier(0,0,0.2,1) infinite",
                      }}
                    />
                    <span
                      className="relative inline-flex rounded-full w-2 h-2"
                      style={{ backgroundColor: "#4AB464" }}
                    />
                  </span>
                  Open to Work
                </div>
              </div>

              {/* Eyebrow */}
              <div className="flex items-center gap-3 mb-8">
                <span
                  className="font-dm text-[11px] uppercase tracking-[0.16em]"
                  style={{ color: "#C17D3C" }}
                >
                  Boise, ID — Open to remote
                </span>
              </div>

              {/* Headline */}
              <h1
                className="font-lora font-semibold leading-[1.15] tracking-tight mb-6"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  color: "#FAF8F4",
                }}
              >
                The product mind<br />
                and the{" "}
                <em style={{ color: "#C17D3C", fontStyle: "italic" }}>engineering depth</em>
                {" "}—<br />
                built to ship things<br />
                that matter.
              </h1>

              <p
                className="font-dm text-[15px] leading-[1.75] mb-10"
                style={{ color: "rgba(250,248,244,0.55)", maxWidth: "420px" }}
              >
                Customer-obsessed. Technically grounded. Deeply invested in the people I lead.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-5">
                <button
                  onClick={() => scrollTo("contact")}
                  className="font-dm font-medium text-[14px] rounded-full px-8 py-3.5 transition-all duration-200 hover:-translate-y-px whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: "#C17D3C", color: "#FAF8F4" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#A8692E"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#C17D3C"; }}
                >
                  Get in touch
                </button>
                <a
                  href="https://storage.readdy-site.link/project_files/661f58d1-4775-43ab-846c-b8afe4ff99b3/260837e4-7be0-4bd2-a51f-b0576434f42d_Josue_Tello_Resume_2026_Updated.pdf?v=6800c1e98ecd4a24fffe3c6c63ba4d5c"
                  download
                  className="font-dm font-medium text-[14px] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  style={{ color: "rgba(250,248,244,0.45)", textDecoration: "underline", textDecorationColor: "rgba(250,248,244,0.2)", textUnderlineOffset: "4px" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "#C17D3C";
                    (e.currentTarget as HTMLAnchorElement).style.textDecorationColor = "#C17D3C";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "rgba(250,248,244,0.45)";
                    (e.currentTarget as HTMLAnchorElement).style.textDecorationColor = "rgba(250,248,244,0.2)";
                  }}
                >
                  Download resume
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* MARQUEE TICKER — bottom of hero */}
      <div
        className="w-full overflow-hidden py-5 flex-shrink-0"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div
          className="flex gap-0 whitespace-nowrap"
          style={{ animation: "marquee 28s linear infinite" }}
        >
          {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-5 font-dm text-[12px] uppercase tracking-[0.12em] px-8" style={{ color: "rgba(250,248,244,0.3)" }}>
              {item}
              <span style={{ color: "#C17D3C", fontSize: "6px" }}>◆</span>
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
